'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils'
import { DropdownMenu, type DropdownMenuProps } from '../DropDownMenu'
import { Button, ButtonProps } from '../Button'
import {
  PopoverAction,
  type PopoverActionProps,
  type UploadCsvContent,
} from '../PopoverAction'
import { UploadCsvPanel } from '../PopoverAction/UploadCsvPanel'
import s from './TopNavigation.module.css'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from '../BreadCrumb'
import { SlashIcon } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback, AvatarProps } from '../Avatar'
import Icon, { type iconTypes } from '../Icon'
import {
  QueryBar,
  QueryBarProps,
  buildLayoutOptionsPanelProps,
} from '../QueryBar'
import { ResponsivePopover } from '../ResponsivePopover'
import { PageDrawer } from '../PageDrawer'
import { type BaseQueryPanelProps } from '../BaseQueryPanel'
import { BreadcrumbTrigger } from '../BreadcrumbTrigger'
import {
  type ViewType,
  type LayoutOptionsPanelProps,
} from '../LayoutOptionsPanel'

// Sidebar: py-1 shell + SidebarContent pt-3 (workspace menu) = 16px before first nav row
const PAGE_DRAWER_NAV_ROW_CLASS = 'h-7 min-h-7 items-center'
const PAGE_DRAWER_NAV_SHELL_CLASS = 'h-auto min-h-0 justify-center !pt-4 !pb-4'

const topNavigationVariants = cva(
  'h-12 flex flex-col items-center bg-background/20 backdrop-blur-md border-accent justify-between shadow-none w-full px-4 z-10 pt-2',
  {
    variants: {
      variant: {
        inline: 'h-12 relative',
        pageDrawer: 'h-auto min-h-0 relative justify-center',
        default: 'h-16 py-2',
        slim: 'h-16 py-2 border-b',
        querybar: 'h-12',
      },
      size: {
        sm: 'h-12',
        md: 'h-12',
        lg: 'h-12',
      },
      bgColor: {
        primary: 'bg-primary',
        white: 'bg-white',
        secondary: 'bg-secondary',
      },
    },
  },
)

type ButtonAction = {
  component: 'button'
  shrinkOnScroll?: boolean
  props: ButtonProps
}

type DropdownAction = {
  component: 'dropdown'
  shrinkOnScroll?: boolean
  props: DropdownMenuProps
}

type PopoverActionAction = {
  component: 'popover-action'
  shrinkOnScroll?: boolean
  props: PopoverActionProps
}

type NavigationActionComponent =
  | ButtonAction
  | DropdownAction
  | PopoverActionAction

function EditableBreadcrumbLabel({
  label,
  onLabelChange,
  className,
}: {
  label: string
  onLabelChange: (label: string) => void | Promise<void>
  className?: string
}) {
  const [isEditing, setIsEditing] = React.useState(false)
  const [editValue, setEditValue] = React.useState(label)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!isEditing) {
      setEditValue(label)
    }
  }, [label, isEditing])

  React.useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  const handleSave = () => {
    const next = editValue.trim()
    setIsEditing(false)
    if (next && next !== label) {
      void onLabelChange(next)
    }
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={editValue}
        onChange={(event) => setEditValue(event.target.value)}
        onBlur={handleSave}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleSave()
          } else if (event.key === 'Escape') {
            setIsEditing(false)
            setEditValue(label)
          }
        }}
        className={cn(
          'min-w-[8rem] max-w-[20rem] bg-transparent border-b border-border text-sm font-medium text-foreground outline-none',
          className,
        )}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      className={cn(
        'group inline-flex items-center gap-1.5 truncate text-sm font-medium text-foreground hover:text-foreground/90',
        className,
      )}
    >
      <span className="truncate">{label}</span>
      <Icon
        name="Pen"
        size={12}
        className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
      />
    </button>
  )
}

export interface PageDrawerConfig {
  pageId?: string
  page: {
    name: string
    icon?: iconTypes
  }
  count?: number
  modelName?: string
  modelId?: string
  modelOptions?: { value: string; label: string }[]
  onLoadModelOptions?: () => Promise<{ value: string; label: string }[]>
  onPageModelChange?: (
    modelId: string,
    modelLabel?: string,
  ) => void | Promise<void>
  currentView: ViewType
  onViewChange?: (view: ViewType) => void
  layoutContent?: React.ReactNode
  layoutProps?: Omit<LayoutOptionsPanelProps, 'view' | 'onViewChange'>
  importContent?: React.ReactNode
  importUploadContent?: UploadCsvContent
  onImportClick?: () => void
  onPageNameChange?: (name: string) => void | Promise<void>
  baseQueryPanel?: BaseQueryPanelProps
  onShareWithTeam?: () => void | Promise<void>
  showShareWithTeam?: boolean
}

export interface TopNavigationProps
  extends VariantProps<typeof topNavigationVariants> {
  bgColor?: 'primary' | 'secondary' | 'white'
  browserTitle?: string
  workplaceName?: string
  breadcrumb?: {
    items: {
      isLoading?: boolean
      avatar?: AvatarProps
      label: string
      href?: string
      alwaysVisible?: boolean
      // Optional: render this breadcrumb item as a dropdown instead of a link
      dropdown?: DropdownMenuProps
      // Optional: render this breadcrumb item as a popover trigger
      popoverContent?: React.ReactNode
      popoverTitle?: string
      onLabelChange?: (label: string) => void | Promise<void>
    }[]
  }
  pageVariant?: 'list' | 'object'
  leftActions?: NavigationActionComponent[]
  rightActions?: NavigationActionComponent[]
  rightSlot?: React.ReactNode
  logo?: {
    src: string
    alt: string
    href?: string
  }
  queryBar?: QueryBarProps | null
  // Page drawer configuration for list pages
  pageDrawer?: PageDrawerConfig | null
}

const generateActionComponent = (
  action: NavigationActionComponent,
  id: number,
  position: 'left' | 'right',
  variant: 'default' | 'slim' | 'inline' | 'querybar' | 'pageDrawer',
) => {
  if (action.component === 'button') {
    return <Button key={`${position}-${id}`} {...action.props} />
  }
  if (action.component === 'dropdown') {
    return <DropdownMenu key={`${position}-${id}`} {...action.props} />
  }
  if (action.component === 'popover-action') {
    return <PopoverAction key={`${position}-${id}`} {...action.props} />
  }
}

const TopNavigation = React.forwardRef<HTMLDivElement, TopNavigationProps>(
  (
    {
      variant,
      size,
      pageVariant,
      bgColor,
      logo,
      leftActions,
      rightActions,
      rightSlot,
      breadcrumb,
      queryBar,
      pageDrawer,
    },
    ref,
  ) => {
    const [isExiting, setIsExiting] = React.useState(false)
    const [nextItems, setNextItems] = React.useState(breadcrumb?.items)
    const prevItems = React.useRef(breadcrumb?.items || [])
    const prevItemsString = React.useRef('')
    const [isQueryBarActive, setIsQueryBarActive] = React.useState(false)

    const [showQueryBar, setShowQueryBar] = React.useState(false)

    // Sync active state from controlled QueryBar state
    React.useEffect(() => {
      if (queryBar && typeof queryBar.state?.isActive !== 'undefined') {
        setIsQueryBarActive(Boolean(queryBar.state.isActive))
      }
    }, [queryBar?.state?.isActive, queryBar])

    // Set query bar active state based on page variant
    React.useEffect(() => {
      if (pageVariant === 'list' && queryBar) {
        setShowQueryBar(true)
      } else {
        setShowQueryBar(false)
      }
    }, [pageVariant, queryBar])

    // Helper to check if we need to animate based on path changes
    const shouldAnimate = (
      oldItems: NonNullable<typeof breadcrumb>['items'],
      newItems: NonNullable<typeof breadcrumb>['items'],
    ) => {
      if (!oldItems || !newItems) return false
      if (oldItems.length !== newItems.length) return true

      // Check if any paths changed
      for (let i = 0; i < oldItems.length; i++) {
        if (oldItems[i]?.href !== newItems[i]?.href) return true
      }
      return false
    }

    React.useEffect(() => {
      if (!breadcrumb?.items) return

      // Create a string representation without non-serializable properties
      const itemsString = JSON.stringify(
        breadcrumb.items.map((item) => ({
          ...item,
          isLoading: undefined,
          dropdown: undefined,
          popoverContent: undefined,
        })),
      )

      // Only trigger animation if items actually changed (ignoring loading state)
      if (
        prevItemsString.current !== itemsString &&
        shouldAnimate(prevItems.current, breadcrumb.items)
      ) {
        // Store the new items but don't show them yet
        setNextItems(breadcrumb.items)
        setIsExiting(true)

        // Wait for fade out
        setTimeout(() => {
          // Update the prev items after fade out
          prevItems.current = breadcrumb.items
          prevItemsString.current = itemsString
          setIsExiting(false)
          setNextItems(undefined)
        }, 150) // Just the fade out duration
      } else {
        // If only loading states changed or no path changes, update prevItems without animation
        prevItems.current = breadcrumb.items
        prevItemsString.current = itemsString
      }
    }, [breadcrumb?.items])

    const currentItems = isExiting
      ? prevItems.current
      : nextItems || breadcrumb?.items || []

    // Determine if we should use the new page drawer mode
    const usePageDrawerMode = pageVariant === 'list' && pageDrawer
    const [pageDrawerOpen, setPageDrawerOpen] = React.useState(false)
    const pageDrawerImportContent =
      pageDrawer?.importContent ??
      (pageDrawer?.importUploadContent ? (
        <UploadCsvPanel content={pageDrawer.importUploadContent} />
      ) : undefined)

    const pageDrawerLayoutProps =
      pageDrawer?.layoutProps ??
      (queryBar ? buildLayoutOptionsPanelProps(queryBar) : undefined)

    const pageDrawerOnViewChange =
      pageDrawer?.onViewChange ??
      (queryBar
        ? (view: ViewType) =>
            queryBar.onStateChange({ ...queryBar.state, view }, 'view')
        : undefined)

    const pageDrawerCurrentView =
      (queryBar?.state?.view as ViewType | undefined) ??
      pageDrawer?.currentView ??
      'spreadsheet'

    const pageDrawerQueryBar = queryBar && pageDrawer ? queryBar : undefined

    return (
      <div
        ref={ref}
        className={cn(
          topNavigationVariants({
            variant: usePageDrawerMode
              ? 'pageDrawer'
              : isQueryBarActive
                ? 'default'
                : variant,
            bgColor,
          }),
          s.navAnimation,
          // Page drawer: mirror sidebar nav shell (py-1) + item row (h-7)
          usePageDrawerMode && PAGE_DRAWER_NAV_SHELL_CLASS,
          // Override height when query bar is present to allow for vertical layout
          queryBar && !usePageDrawerMode && 'h-auto min-h-12 pb-0',
        )}
      >
        {/* Top navigation actions */}
        <div
          className={cn(
            'flex flex-row w-full justify-between items-center gap-2',
            usePageDrawerMode ? PAGE_DRAWER_NAV_ROW_CLASS : { 'h-12': !size },
          )}
        >
          <div className="flex flex-row gap-2 items-center flex-1">
            {leftActions && (
              <div className="flex flex-row gap-1 md:hidden">
                {leftActions.map((action, id) =>
                  generateActionComponent(
                    action,
                    id,
                    'left',
                    variant || 'default',
                  ),
                )}
              </div>
            )}
            <div
              className={cn(
                'flex flex-row flex-1 items-center gap-2',
                usePageDrawerMode ? 'min-w-0' : 'overflow-hidden',
              )}
            >
              {/* Page drawer mode for list pages */}
              {usePageDrawerMode && pageDrawer && breadcrumb && (
                <Breadcrumb className="min-w-0 overflow-visible">
                  <BreadcrumbList
                    className={cn(
                      'relative flex min-w-0 flex-1 items-center gap-2',
                      s.breadcrumbContainer,
                      s.scaleOrigin,
                      { 'scale-90': variant === 'slim' },
                    )}
                  >
                    {breadcrumb.items.slice(0, -1).map((item, index) => (
                      <BreadcrumbItem
                        key={`${item.label}-${index}-parent`}
                        className={cn(
                          s.breadcrumbItem,
                          s.breadcrumbEnter,
                          s[`delay${index}`],
                          item.alwaysVisible ? 'flex' : 'hidden md:flex',
                          'items-center whitespace-nowrap',
                        )}
                      >
                        <BreadcrumbLink
                          href={item.href ?? '#'}
                          className="inline-flex items-center gap-2"
                        >
                          {item.avatar &&
                            (item.avatar.name === 'RSS' ? (
                              <div className="h-5 w-5 shrink-0 rounded-full bg-muted flex items-center justify-center">
                                <Icon
                                  name="Rss"
                                  size={12}
                                  className="text-muted-foreground"
                                />
                              </div>
                            ) : (
                              <Avatar className="h-5 w-5 shrink-0">
                                <AvatarImage seed={item.avatar.seed} />
                                <AvatarFallback className="text-[10px] font-medium">
                                  {item.avatar.name}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                          <span className="truncate">{item.label}</span>
                        </BreadcrumbLink>
                        <BreadcrumbSeparator className="shrink-0 ml-2">
                          <SlashIcon className="h-4 w-4" />
                        </BreadcrumbSeparator>
                      </BreadcrumbItem>
                    ))}
                    <BreadcrumbItem
                      className={cn(
                        s.breadcrumbItem,
                        s.breadcrumbEnter,
                        s[`delay${Math.max(0, breadcrumb.items.length - 1)}`],
                        'flex min-w-0 items-center overflow-visible whitespace-nowrap gap-1.5',
                      )}
                    >
                      <PageDrawer
                        page={pageDrawer.page}
                        count={pageDrawer.count}
                        modelName={pageDrawer.modelName}
                        modelId={pageDrawer.modelId}
                        modelOptions={pageDrawer.modelOptions}
                        onLoadModelOptions={pageDrawer.onLoadModelOptions}
                        onPageModelChange={pageDrawer.onPageModelChange}
                        currentView={pageDrawerCurrentView}
                        onViewChange={pageDrawerOnViewChange}
                        layoutContent={pageDrawer.layoutContent}
                        layoutProps={pageDrawerLayoutProps}
                        importContent={pageDrawerImportContent}
                        onImportClick={pageDrawer.onImportClick}
                        onPageNameChange={pageDrawer.onPageNameChange}
                        baseQueryPanel={pageDrawer.baseQueryPanel}
                        onShareWithTeam={pageDrawer.onShareWithTeam}
                        showShareWithTeam={pageDrawer.showShareWithTeam}
                        queryBar={pageDrawerQueryBar}
                        open={pageDrawerOpen}
                        onOpenChange={setPageDrawerOpen}
                        trigger={
                          <BreadcrumbTrigger
                            pageName={pageDrawer.page.name}
                            count={pageDrawer.count}
                            isOpen={pageDrawerOpen}
                          />
                        }
                      />
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              )}
              {/* Page drawer mode without breadcrumb trail */}
              {usePageDrawerMode && pageDrawer && !breadcrumb && (
                <div className="flex min-w-0 items-center gap-1.5 overflow-visible text-sm">
                  <PageDrawer
                    page={pageDrawer.page}
                    count={pageDrawer.count}
                    modelName={pageDrawer.modelName}
                    modelId={pageDrawer.modelId}
                    modelOptions={pageDrawer.modelOptions}
                    onLoadModelOptions={pageDrawer.onLoadModelOptions}
                    onPageModelChange={pageDrawer.onPageModelChange}
                    currentView={pageDrawerCurrentView}
                    onViewChange={pageDrawerOnViewChange}
                    layoutContent={pageDrawer.layoutContent}
                    layoutProps={pageDrawerLayoutProps}
                    importContent={pageDrawerImportContent}
                    onImportClick={pageDrawer.onImportClick}
                    onPageNameChange={pageDrawer.onPageNameChange}
                    baseQueryPanel={pageDrawer.baseQueryPanel}
                    onShareWithTeam={pageDrawer.onShareWithTeam}
                    showShareWithTeam={pageDrawer.showShareWithTeam}
                    queryBar={pageDrawerQueryBar}
                    open={pageDrawerOpen}
                    onOpenChange={setPageDrawerOpen}
                    trigger={
                      <BreadcrumbTrigger
                        pageName={pageDrawer.page.name}
                        count={pageDrawer.count}
                        isOpen={pageDrawerOpen}
                      />
                    }
                  />
                </div>
              )}
              {/* Standard breadcrumb mode */}
              {!usePageDrawerMode && breadcrumb && (
                <Breadcrumb>
                  <BreadcrumbList
                    className={cn(
                      'relative flex items-center gap-2 overflow-hidden',
                      s.breadcrumbContainer,
                      s.scaleOrigin,
                      { 'scale-90': variant === 'slim' },
                    )}
                  >
                    {/* Render exiting items */}
                    {isExiting &&
                      prevItems.current.map((item, index) => (
                        <BreadcrumbItem
                          key={`${item.label}-${index}-exit`}
                          className={cn(
                            s.breadcrumbItem,
                            s.breadcrumbExit,
                            s[`delay${index}`],
                            item.alwaysVisible ? 'flex' : 'hidden md:flex',
                            'items-center whitespace-nowrap',
                          )}
                        >
                          {item.dropdown ? (
                            <DropdownMenu {...item.dropdown} />
                          ) : (
                            <BreadcrumbLink
                              href={item.href ?? '#'}
                              className="inline-flex items-center gap-2"
                            >
                              {item.isLoading ? (
                                <div className="h-4 w-24 bg-muted animate-pulse rounded-lg" />
                              ) : (
                                <>
                                  {item.avatar &&
                                    (item.avatar.name === 'RSS' ? (
                                      <div className="h-5 w-5 shrink-0 rounded-full bg-muted flex items-center justify-center">
                                        <Icon
                                          name="Rss"
                                          size={12}
                                          className="text-muted-foreground"
                                        />
                                      </div>
                                    ) : (
                                      <Avatar className="h-5 w-5 shrink-0">
                                        <AvatarImage seed={item.avatar.seed} />
                                        <AvatarFallback className="text-[10px] font-medium">
                                          {item.avatar.name}
                                        </AvatarFallback>
                                      </Avatar>
                                    ))}
                                  <span className="truncate">{item.label}</span>
                                </>
                              )}
                            </BreadcrumbLink>
                          )}
                          {index < prevItems.current.length - 1 && (
                            <BreadcrumbSeparator className="shrink-0 ml-2">
                              <SlashIcon className="h-4 w-4" />
                            </BreadcrumbSeparator>
                          )}
                        </BreadcrumbItem>
                      ))}
                    {/* Render entering items */}
                    {!isExiting &&
                      breadcrumb.items.map((item, index) => {
                        const content = item.isLoading ? (
                          <div className="h-4 w-24 bg-muted animate-pulse rounded-lg" />
                        ) : item.onLabelChange ? (
                          <EditableBreadcrumbLabel
                            label={item.label}
                            onLabelChange={item.onLabelChange}
                          />
                        ) : (
                          <>
                            {item.avatar &&
                              (item.avatar.name === 'RSS' ? (
                                <div className="h-5 w-5 shrink-0 rounded-full bg-muted flex items-center justify-center">
                                  <Icon
                                    name="Rss"
                                    size={12}
                                    className="text-muted-foreground"
                                  />
                                </div>
                              ) : (
                                <Avatar className="h-5 w-5 shrink-0">
                                  <AvatarImage seed={item.avatar.seed} />
                                  <AvatarFallback className="text-[10px] font-medium">
                                    {item.avatar.name}
                                  </AvatarFallback>
                                </Avatar>
                              ))}
                            <span className="truncate">{item.label}</span>
                          </>
                        )

                        return (
                          <BreadcrumbItem
                            key={`${item.label}-${index}-enter`}
                            className={cn(
                              s.breadcrumbItem,
                              s.breadcrumbEnter,
                              s[`delay${index}`],
                              item.alwaysVisible ? 'flex' : 'hidden md:flex',
                              'items-center whitespace-nowrap',
                            )}
                          >
                            {item.dropdown ? (
                              <DropdownMenu {...item.dropdown} />
                            ) : item.popoverContent ? (
                              <ResponsivePopover
                                trigger={
                                  <button className="inline-flex items-center gap-2 hover:text-foreground transition-colors text-muted-foreground">
                                    {content}
                                  </button>
                                }
                                title={item.popoverTitle ?? item.label}
                              >
                                {item.popoverContent}
                              </ResponsivePopover>
                            ) : item.onLabelChange ? (
                              content
                            ) : (
                              <BreadcrumbLink
                                href={item.href ?? '#'}
                                className="inline-flex items-center gap-2"
                              >
                                {content}
                              </BreadcrumbLink>
                            )}
                            {index < breadcrumb.items.length - 1 && (
                              <BreadcrumbSeparator className="shrink-0 ml-2">
                                <SlashIcon className="h-4 w-4" />
                              </BreadcrumbSeparator>
                            )}
                          </BreadcrumbItem>
                        )
                      })}
                  </BreadcrumbList>
                </Breadcrumb>
              )}
            </div>
          </div>
          {rightSlot || rightActions ? (
            <div className="flex shrink-0 flex-row items-center gap-2">
              {rightSlot}
              {rightActions ? (
                <div className="flex flex-row items-center gap-1">
                  {rightActions.map((action, id) =>
                    generateActionComponent(
                      action,
                      id,
                      'right',
                      variant || 'default',
                    ),
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Query bar row - positioned below breadcrumbs and actions */}
        {/* Only show when not using page drawer mode */}
        {queryBar && showQueryBar && !usePageDrawerMode && (
          <div className="w-full">
            <QueryBar {...queryBar} />
          </div>
        )}
      </div>
    )
  },
)
TopNavigation.displayName = 'TopNavigation'

export { TopNavigation, topNavigationVariants }
